/**
 * @fileoverview Barra superior da aplicação (top bar).
 *
 * Exibe breadcrumbs, campo de busca (Cmd+K), notificações,
 * painel de novidades, toggle de tema e menu do perfil do usuário.
 * Adapta-se automaticamente entre desktop (botão collapse) e mobile (botão hamburguer).
 */
import { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ChevronLeft, Moon, Sun, Monitor, Search, Command, UserCircle, Settings, LogOut, Sparkles, Check } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import FocoDoDia from '@/components/FocoDoDia';
import { useStore } from '@/store/useStore';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { AppNotifications } from '@/components/layout/AppNotifications';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { getInitials, avatarFallbackStyle } from '@/lib/utils';
import { STORAGE_KEYS } from '@/lib/constants';

// Hoisted to module scope — `lazy()` inside the component body re-creates
// the wrapper on every render, defeating Suspense's memoization and causing
// occasional Sheet open-flash. One lazy reference reused across renders.
const WhatsNew = lazy(() => import('@/components/WhatsNew'));

/**
 * Theme selector (U-03 da auditoria → U-09): dropdown com os 3 estados
 * explícitos (Sistema / Claro / Escuro) em vez de um botão de ciclo sem
 * rótulo. O ícone do gatilho reflete o modo escolhido (sol = claro, lua =
 * escuro, monitor = sistema) e a opção atual recebe um check.
 */
const THEME_OPTIONS = [
  { mode: 'system' as const, label: 'Sistema', icon: Monitor },
  { mode: 'light' as const, label: 'Claro', icon: Sun },
  { mode: 'dark' as const, label: 'Escuro', icon: Moon },
];

function ThemeToggleButton() {
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const Icon = theme === 'dark' ? Sun : theme === 'light' ? Moon : Monitor;
  const currentLabel = theme === 'system' ? 'sistema' : theme === 'light' ? 'claro' : 'escuro';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground h-8 w-8"
          aria-label={`Tema (atual: ${currentLabel})`}
          title={`Tema: ${currentLabel}`}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {THEME_OPTIONS.map(({ mode, label, icon: OptIcon }) => (
          <DropdownMenuItem key={mode} onClick={() => setTheme(mode)} className="gap-2 cursor-pointer">
            <OptIcon className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1">{label}</span>
            {theme === mode && <Check className="h-3.5 w-3.5 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Estrutura de uma notificação recebida pelo sistema */
interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string | null;
  is_read: boolean;
  created_at: string;
  entity_type?: string | null;
  entity_id?: string | null;
}

/**
 * Props da barra superior. Tema é lido diretamente do store dentro de
 * ThemeToggleButton — não precisa ser passado por aqui.
 */
interface AppTopBarProps {
  collapsed: boolean;
  setCollapsed: (v: boolean | ((prev: boolean) => boolean)) => void;
  isMobile: boolean;
  setMobileOpen: (v: boolean) => void;
  userName: string;
  userEmail: string;
  userAvatar: string;
  handleLogout: () => void;
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  pushEnabled: boolean;
  enablePush: () => void;
}

/**
 * Barra superior com breadcrumbs, busca, notificações, novidades, tema e perfil.
 * O botão de busca dispara o Command Palette via evento sintético de teclado.
 */
export function AppTopBar({
  collapsed,
  setCollapsed,
  isMobile,
  setMobileOpen,
  userName,
  userEmail,
  userAvatar,
  handleLogout,
  notifications,
  unreadCount,
  markAsRead,
  markAllAsRead,
  clearAll,
  pushEnabled,
  enablePush,
}: AppTopBarProps) {
  const navigate = useNavigate();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  return (
    <header className="h-12 md:h-14 border-b border-border flex items-center justify-between px-3 md:px-5 bg-card/80 backdrop-blur-sm shrink-0">
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        {!isMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => setCollapsed(prev => {
                  const next = !prev;
                  localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(next));
                  return next;
                })}
                aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                {collapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">{collapsed ? 'Expandir menu' : 'Recolher menu'}</TooltipContent>
          </Tooltip>
        )}
        {isMobile && (
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Abrir menu" onClick={() => setMobileOpen(true)}>
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <Breadcrumbs />
      </div>

      <div className="flex items-center gap-1.5">
        <button
          onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/40 text-muted-foreground text-sm w-48 hover:bg-muted/60 transition-colors cursor-pointer"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="text-xs">Buscar...</span>
          <div className="ml-auto flex items-center gap-0.5 text-[11px] border border-border rounded px-1 py-0.5">
            <Command className="h-2.5 w-2.5" /> K
          </div>
        </button>

        <AppNotifications
          notifications={notifications}
          unreadCount={unreadCount}
          markAsRead={markAsRead}
          markAllAsRead={markAllAsRead}
          clearAll={clearAll}
          pushEnabled={pushEnabled}
          enablePush={enablePush}
        />

        <FocoDoDia />

        <Sheet open={whatsNewOpen} onOpenChange={setWhatsNewOpen}>
          <SheetTrigger asChild>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8 relative" aria-label="Novidades">
                  <Sparkles className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Novidades</TooltipContent>
            </Tooltip>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-secondary" /> Novidades
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="h-8 w-8 border-2 border-secondary border-t-transparent rounded-full animate-spin" /></div>}>
                <WhatsNew />
              </Suspense>
            </div>
          </SheetContent>
        </Sheet>

        <ThemeToggleButton />


        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label={`Menu do perfil de ${userName}`}
            >
              <Avatar className="h-7 w-7 cursor-pointer ring-2 ring-secondary/20 hover:ring-secondary/40 transition-all">
                <AvatarImage src={userAvatar} alt={userName} />
                <AvatarFallback
                  className="text-2xs font-bold"
                  style={avatarFallbackStyle(userName)}
                >
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="end" sideOffset={8}>
            <div className="flex items-center gap-2.5 p-2 mb-1">
              <UserAvatar name={userName} avatarUrl={userAvatar} size="md" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{userName}</p>
                <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
              </div>
            </div>
            <Separator className="my-1" />
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/configuracoes?tab=perfil')}>
              <UserCircle className="h-4 w-4" /> Meu Perfil
            </Button>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/configuracoes')}>
              <Settings className="h-4 w-4" /> Configurações
            </Button>
            <Separator className="my-1" />
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-sm text-destructive hover:text-destructive" onClick={handleLogout}>
              <LogOut className="h-4 w-4" /> Sair
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}
